/* eslint-disable */
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "react-bootstrap/Button";
import {
  getAuth,
  signInWithEmailAndPassword,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";
import { useDispatch } from "react-redux";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  setDoc,
} from "firebase/firestore";
import { db } from "./../../firebase";
import { loginUser } from "../../redux/user/user";
import { fetchAccount } from "../../redux/account/account";
import { fetchWorker } from "../../redux/worker/worker";
import { useTranslation } from "react-i18next";
import DarkModeToggle from "../../components/navbar/DarkModeToggle";

function Login() {
  const auth = getAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  useEffect(() => {
    const savedLanguage = localStorage.getItem("language");
    if (savedLanguage && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
    localStorage.setItem("language", language);
  };

  const [loginForm, setLoginForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
  });

  const [hidePassword, setHidePassword] = useState(true);
  const [usePasswordless, setUsePasswordless] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleUserLogin = async (user) => {
    try {
      const token = await user.getIdToken();
      const accountQuery = query(
        collection(db, "account"),
        where("user", "==", user.uid),
      );
      const accountSnapshot = await getDocs(accountQuery);
      if (!accountSnapshot.empty) {
        const accountDoc = accountSnapshot.docs[0];
        const accountData = { id: accountDoc.id, ...accountDoc.data() };

        await setDoc(doc(db, "account", accountDoc.id), {
          ...accountData,
          isLoggedIn: true,
        });

        dispatch(fetchAccount(accountData));
        dispatch(loginUser(user.uid));
        localStorage.setItem("token", user.uid);
        localStorage.setItem("auth-token", token);

        if (accountData.language) {
          localStorage.setItem("language", accountData.language);
          i18n.changeLanguage(accountData.language);
        }

        setError(false);
        setLoading(false);

        navigate("/dashboard");
        return;
      }
      const workerQuery = query(
        collection(db, "workers"),
        where("user", "==", user.uid),
      );
      const workerSnapshot = await getDocs(workerQuery);

      if (!workerSnapshot.empty) {
        const workerDoc = workerSnapshot.docs[0];
        const workerData = { id: workerDoc.id, ...workerDoc.data() };

        await setDoc(doc(db, "workers", workerDoc.id), {
          ...workerData,
          isLoggedIn: true,
        });

        dispatch(fetchWorker(workerData));
        dispatch(loginUser(user.uid));
        localStorage.setItem("token", user.uid);
        localStorage.setItem("auth-token", token);

        if (workerData.language) {
          localStorage.setItem("language", workerData.language);
          i18n.changeLanguage(workerData.language);
        }

        setError(false);
        setLoading(false);

        navigate("/team/dashboard");
        return;
      }

      setError(true);
      setErrorMessage(t("login.auth.error_no_profile"));
      setLoading(false);
      await auth.signOut();
    } catch (error) {
      console.error("Error handling user login:", error);
      setError(true);
      setErrorMessage(t("login.auth.error_general"));
      setLoading(false);
    }
  };

  // sign in without password (passwordless)
  useEffect(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      let email = window.localStorage.getItem("emailForSignIn");

      if (email) {
        signInWithEmailLink(auth, email, window.location.href)
          .then(async (result) => {
            window.localStorage.removeItem("emailForSignIn");
            await handleUserLogin(result.user);
          })
          .catch((error) => {
            console.error("Error signing in with email link", error);
            setError(true);
            setErrorMessage(t("login.auth.error_link"));
          });
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(false);
    setErrorMessage("");
    setLoading(true);

    if (usePasswordless) {
      // sign in without password (email link)
      const actionCodeSettings = {
        url: `${window.location.origin}/login`,
        handleCodeInApp: true,
      };

      try {
        await sendSignInLinkToEmail(auth, loginForm.email, actionCodeSettings);
        window.localStorage.setItem("emailForSignIn", loginForm.email);
        setLinkSent(true);
        setLoading(false);
      } catch (error) {
        console.error("Error sending sign in link", error);
        setError(true);
        setErrorMessage(t("login.auth.error_link"));
        setLoading(false);
      }
    } else {
      try {
        const userCredential = await signInWithEmailAndPassword(
          auth,
          loginForm.email,
          loginForm.password,
        );
        await handleUserLogin(userCredential.user);
      } catch (error) {
        console.error("Login error:", error);
        setError(true);
        if (
          error.code === "auth/user-not-found" ||
          error.code === "auth/wrong-password"
        ) {
          setErrorMessage(t("login.auth.error_credentials"));
        } else if (error.code === "auth/too-many-requests") {
          setErrorMessage(t("login.auth.error_too_many_attempts"));
        } else {
          setErrorMessage(t("login.auth.error_general"));
        }

        setLoading(false);
      }
    }
  };

  return (
    <main
      className="w-full flex dark:bg-[#141414] dark:text-gray-200"
      dir={i18n.dir()}
    >
      {/* Left Hero Section */}
      <div className="relative flex-1 hidden items-center justify-center h-screen bg-gray-900 overflow-hidden lg:flex">
        <div className="relative z-10 w-full max-w-md">
          <img src="/logowhite.png" width={150} alt={t("ui.logo")} />
          <div className="mt-16 space-y-3">
            <h3 className="text-white text-3xl font-bold">
              {t("login.hero.headline")}
            </h3>
            <p className="text-gray-300">{t("login.hero.subtext")}</p>

            {/* Avatars + Text */}
            <div className="flex items-center gap-3 -space-x-2 overflow-hidden">
              <div className="flex items-center -space-x-2 overflow-hidden">
                {/* Avatars */}
                {[
                  "https://randomuser.me/api/portraits/women/79.jpg",
                  "https://api.uifaces.co/our-content/donated/xZ4wg2Xj.jpg",
                  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-0.3.5&q=80&fm=jpg&crop=faces&fit=crop&h=200&w=200&s=a72ca28288878f8404a795f39642a46f",
                  "https://randomuser.me/api/portraits/men/86.jpg",
                  "https://images.unsplash.com/photo-1510227272981-87123e259b17?ixlib=rb-0.3.5&q=80&fm=jpg&crop=faces&fit=crop&h=200&w=200&s=3759e09a5b9fbe53088b23c615b6312e",
                ].map((src, idx) => (
                  <img
                    key={idx}
                    src={src}
                    className="rounded-lg w-10 h-10 shadow-sm bg-[#faf1ff] border-2 border-white"
                    alt={t("ui.userNumber", { value0: (idx + 1) })}
                  />
                ))}
              </div>

              <p
                dir={i18n.dir()}
                className={`text-sm text-gray-400 font-medium ${
                  i18n.dir() === "rtl"
                    ? "mr-[-10px] text-right"
                    : "ml-[-10px] text-left"
                }`}
              >
                {t("login.hero.join_merchants")}
              </p>
            </div>
          </div>
        </div>

        {/* Gradient Background */}
        <div
          className="absolute inset-0 my-auto h-[500px]"
          style={{
            background:
              "linear-gradient(152.92deg, rgba(192, 132, 252, 0.2) 4.54%, rgba(232, 121, 249, 0.26) 34.2%, rgba(192, 132, 252, 0.1) 77.55%)",
            filter: "blur(118px)",
          }}
        ></div>
      </div>

      <div className="flex-1 flex items-center justify-center h-screen relative">
        <div className="absolute top-4 w-full px-4 flex justify-between z-50">
          <div className={`${isRTL ? "order-2" : "order-1"}`}>
            <select
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-600 dark:bg-[#1b1b1b] dark:!border-[#3c3a3d] dark:text-gray-200"
              value={i18n.language}
            >
              <option value="ar">{t("login.language.ar")}</option>
              <option value="fr">{t("login.language.fr")}</option>
              <option value="en">{t("login.language.en")}</option>
            </select>
          </div>

          <div className={`${isRTL ? "order-1" : "order-2"}`}>
            <DarkModeToggle />
          </div>
        </div>

        <div className="max-w-sm w-full text-gray-600 space-y-5">
          <div className="text-center pb-8">
            <img src="/Logo.png" width={150} alt={t("ui.logo")} className="mx-auto" />
            <h3 className="text-gray-800 text-2xl font-bold sm:text-3xl dark:text-gray-100 mt-5">
              {t("login.auth.title")}
            </h3>
          </div>

          {linkSent ? (
            // Confirmation message
            <div className="text-center p-6 bg-green-50 rounded-lg dark:bg-green-900/20">
              <h4 className="text-green-600 font-medium">
                {t("login.auth.login_link_sent_title")}
              </h4>
              <p className="mt-2 mb-4 dark:text-gray-300">
                {t("login.auth.login_link_sent_description")}
              </p>
              <button
                onClick={() => setLinkSent(false)}
                className="px-4 py-2 bg-[#9b59bf] text-white rounded-lg hover:bg-indigo-500 transition-colors"
              >
                {t("login.auth.ok")}
              </button>
            </div>
          ) : (
            // Login form
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label className="font-medium dark:text-gray-200">
                  {t("login.auth.email")}
                </label>
                <input
                  value={loginForm.email}
                  onChange={(e) =>
                    setLoginForm({ ...loginForm, email: e.target.value })
                  }
                  type="email"
                  required
                  placeholder={t("login.auth.email_placeholder")}
                  className="w-full mt-2 px-3 py-2 border rounded-lg text-gray-500 bg-transparent outline-none
                         focus:ring-purple-400 shadow-sm  dark:!border-[#3c3a3d] dark:text-gray-200 dark:placeholder-gray-500"
                />
              </div>

              {/* Password */}
              {!usePasswordless && (
                <div>
                  <label className="font-medium dark:text-gray-200">
                    {t("login.auth.password")}
                  </label>
                  <div className="relative">
                    <input
                      type={hidePassword ? "password" : "text"}
                      value={loginForm.password}
                      onChange={(e) =>
                        setLoginForm({ ...loginForm, password: e.target.value })
                      }
                      placeholder="********"
                      required
                      className="w-full mt-2 px-3 py-2 border rounded-lg text-gray-500 bg-transparent outline-none
                             focus:ring-purple-400 shadow-sm  dark:!border-[#3c3a3d] dark:text-gray-200 dark:placeholder-gray-500"
                    />
                    <button
                      type="button"
                      className={`absolute inset-y-0 ${
                        isRTL ? "left-0" : "right-0"
                      } flex items-center px-4 bg-transparent`}
                      onClick={() => setHidePassword(!hidePassword)}
                    >
                      {/* Eye Icons */}
                      {hidePassword ? (
                        <svg
                          className="w-6 h-6"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-6 h-6"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:border-red-800">
                  <p className="text-red-600 dark:text-red-400 text-sm">
                    {errorMessage ||
                      (usePasswordless
                        ? t("login.auth.error_link")
                        : t("login.auth.error_credentials"))}
                  </p>
                </div>
              )}

              {/* Toggle Link / Password */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-[#9e68ba] hover:text-[#55266d]"></div>
                {!usePasswordless && (
                  <Link
                    to={`/reset-password`}
                    className="text-sm text-[#9e68ba] hover:text-[#55266d]"
                  >
                    {t("login.auth.forgot_password")}
                  </Link>
                )}
              </div>

              {/* Submit Button */}
              <Button
                variant="primary"
                className="w-full px-4 py-2 text-white font-medium bg-[#9b59bf] hover:bg-indigo-500 active:bg-[#9b59bf] rounded-lg duration-150 flex items-center justify-center gap-2"
                type="submit"
                disabled={loading}
              >

                {usePasswordless
                  ? t("login.auth.send_link")
                  : t("login.auth.login")}
              </Button>
            </form>
          )}

          {/* Sign Up link */}
          <p className="text-center dark:text-gray-300">
            {t("login.auth.no_account")}{" "}
            <Link
              to={`/sign-up`}
              className="font-medium text-[#9e68ba] hover:text-[#55266d]"
            >
              {t("login.auth.sign_up")}
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}

export default Login;
